import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../utils/supabase/client';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { User, Building, ShieldCheck, Calendar, Mail, Edit, LogIn, UserX, UserCheck, ShieldQuestion } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';

interface Profile {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  created_at: string;
  organization: {
    name: string;
    subscription: {
      plan: string;
      status: string;
    } | null;
  } | null;
  status: string;
  users: {
    email_confirmed_at: string | null;
  } | null;
}

export default function UserProfilePage() {
  const { userId } = useParams<{ userId: string }>();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const { toast } = useToast();

  // Add state for form fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState('');

  useEffect(() => {
    if (profile) {
      setFirstName(profile.first_name || '');
      setLastName(profile.last_name || '');
      setRole(profile.role || '');
    }
  }, [profile]);

  useEffect(() => {
    async function fetchUserProfile() {
      if (!userId) return;

      try {
        const { data, error } = await supabase.functions.invoke('get-user-details', {
          body: { userId }
        });

        if (error) throw error;
        setProfile(data);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchUserProfile();
  }, [userId]);

  const handleUpdateUser = async () => {
    if (!profile) return;
    try {
      const { data, error } = await supabase.functions.invoke('update-user-details', {
        body: { 
          userId: profile.id,
          firstName,
          lastName,
          role
        }
      });

      if (error) throw error;
      
      setProfile(data);
      toast({
        title: "Success",
        description: "User profile has been updated.",
      });
    } catch (err: any) {
      toast({
        title: "Error",
        description: `Failed to update user: ${err.message}`,
        variant: "destructive",
      });
    } finally {
      setIsEditDialogOpen(false);
    }
  };

  const handleSuspendUser = async () => {
    if (!profile) return;
    const newStatus = profile.status === 'active' ? 'suspended' : 'active';
    try {
      const { data, error } = await supabase.functions.invoke('update-user-status', {
        body: { 
          userId: profile.id,
          status: newStatus
        }
      });

      if (error) throw error;
      
      setProfile(data);
      toast({
        title: "Success",
        description: `User has been ${newStatus}.`,
      });
    } catch (err: any) {
      toast({
        title: "Error",
        description: `Failed to update user status: ${err.message}`,
        variant: "destructive",
      });
    }
  };

  const handleManualVerification = async () => {
    if (!profile) return;
    try {
      const { data, error } = await supabase.functions.invoke('manual-verify-user', {
        body: { userId: profile.id }
      });

      if (error) throw error;
      
      setProfile(data);
      toast({
        title: "Success",
        description: "User has been manually verified.",
      });
    } catch (err: any) {
      toast({
        title: "Error",
        description: `Failed to verify user: ${err.message}`,
        variant: "destructive",
      });
    }
  };

  const handleImpersonate = async () => {
    if (!profile) return;
    try {
      const { data, error } = await supabase.functions.invoke('impersonate-user', {
        body: { userId: profile.id }
      });

      if (error) throw error;
      
      window.open(data.magicLink, '_blank');
      toast({
        title: "Success",
        description: "Magic link generated. Opening in a new tab.",
      });
    } catch (err: any) {
      toast({
        title: "Error",
        description: `Failed to impersonate user: ${err.message}`,
        variant: "destructive",
      });
    }
  };

  const getInitials = (firstName: string | null, lastName: string | null) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  if (loading) return <div className="p-8 text-center">Loading...</div>;
  if (error) return <div className="p-8 text-center text-red-500">{error}</div>;
  if (!profile) return <div className="p-8 text-center">User not found.</div>;

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-start">
        <div className="flex items-center gap-6">
          <Avatar className="h-24 w-24">
            <AvatarFallback className="text-4xl bg-blue-100 dark:bg-gray-700 text-blue-600 dark:text-gray-200">
              {getInitials(profile.first_name, profile.last_name)}
            </AvatarFallback>
          </Avatar>
          <div>
            <h1 className="text-4xl font-bold">{profile.first_name} {profile.last_name}</h1>
            <div className="flex items-center gap-4 mt-2">
              <Badge variant={profile.role === 'admin' ? 'destructive' : 'success'}>{profile.role}</Badge>
              <span className="text-muted-foreground">Joined on {new Date(profile.created_at).toLocaleDateString()}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setIsEditDialogOpen(true)}>
            <Edit className="mr-2 h-4 w-4" />
            Edit User
          </Button>
          <Button variant="outline" onClick={handleImpersonate}>
            <LogIn className="mr-2 h-4 w-4" />
            Log In As User
          </Button>
          {profile.status === 'active' ? (
            <Button variant="destructive" onClick={handleSuspendUser}>
              <UserX className="mr-2 h-4 w-4" />
              Suspend User
            </Button>
          ) : (
            <Button variant="secondary" onClick={handleSuspendUser}>
              <UserCheck className="mr-2 h-4 w-4" />
              Unsuspend User
            </Button>
          )}
          {!profile.users?.email_confirmed_at && (
            <Button variant="outline" onClick={handleManualVerification}>
              <ShieldQuestion className="mr-2 h-4 w-4" />
              Manually Verify User
            </Button>
          )}
        </div>
      </div>

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="firstName" className="text-right">
                First Name
              </Label>
              <Input id="firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)} className="col-span-3" />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="lastName" className="text-right">
                Last Name
              </Label>
              <Input id="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} className="col-span-3" />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="role" className="text-right">
                Role
              </Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleUpdateUser}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <div className="grid md:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><User className="h-5 w-5" /> Personal Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <span>{profile.email}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span>Joined: {new Date(profile.created_at).toLocaleDateString()}</span>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Building className="h-5 w-5" /> Organization</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p>{profile.organization?.name ?? 'No organization'}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5" /> Subscription</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div>Plan: {profile.organization?.subscription?.plan ?? 'N/A'}</div>
            <div className="flex items-center gap-2">
              <span>Status:</span>
              <Badge variant={profile.organization?.subscription?.status === 'active' ? 'success' : 'destructive'}>{profile.organization?.subscription?.status ?? 'N/A'}</Badge>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 